import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTimer } from '../context/TimerContext';
import { useCart } from '../context/CartContext';
import { shopifyApi } from '../api/shopify';
import { CountdownTimer } from '../components/CountdownTimer';
import { Product } from '../types';

export function ProductDetailPage() {
  const { handle } = useParams<{ handle: string }>();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isSiteActive, isExpired } = useTimer();
  const { addToCart, getItemCount } = useCart();

  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [added, setAdded] = useState(false);

  // Fetch product on mount
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const products = await shopifyApi.getProducts();
        const found = products.find((p) => p.handle === handle);
        if (found) {
          setProduct(found);
        } else {
          setError('Product not found');
        }
      } catch (err: any) {
        setError(err.response?.data?.error || err.message || 'Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [handle]);

  // Handle site expiration
  useEffect(() => {
    if (isExpired || !isSiteActive) {
      logout();
      navigate('/');
    }
  }, [isExpired, isSiteActive, logout, navigate]);

  const handleAddToCart = () => {
    if (!product || product.inventoryQuantity <= 0) return;

    addToCart(product);
    setAdded(true);

    setTimeout(() => {
      setAdded(false);
    }, 2000);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const formatPrice = (price: string, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(parseFloat(price));
  };

  return (
    <div className="product-detail-page">
      <header className="products-header">
        <div className="header-left">
          <Link to="/products">
            <img src="/JackpointLogo.avif" alt="Jackpoint" className="header-logo" />
          </Link>
          <p className="user-email">{user?.email}</p>
        </div>
        <div className="header-right">
          <CountdownTimer />
          <Link to="/cart" className="cart-link">
            Cart ({getItemCount()})
          </Link>
          <button onClick={handleLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </header>

      <main className="product-detail-main">
        <div className="breadcrumb">
          <Link to="/products">← Back to Products</Link>
        </div>

        {loading ? (
          <p className="loading">Loading product...</p>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : product ? (
          <div className="product-detail-content">
            <div className="product-detail-image">
              {product.imageUrl ? (
                <img src={product.imageUrl} alt={product.title} />
              ) : (
                <div className="no-image">No image available</div>
              )}
            </div>
            <div className="product-detail-info">
              <h2 className="product-detail-title">{product.title}</h2>
              <p className="product-detail-price">
                {formatPrice(product.price, product.currencyCode)}
              </p>
              {product.description && (
                <div className="product-detail-description">
                  <h3>Description</h3>
                  <div dangerouslySetInnerHTML={{ __html: product.description }} />
                </div>
              )}
              <div className="product-detail-stock">
                <span className={`product-stock ${product.inventoryQuantity <= 0 ? 'out-of-stock' : product.inventoryQuantity <= 5 ? 'low-stock' : ''}`}>
                  {product.inventoryQuantity <= 0 ? 'Out of stock' : `${product.inventoryQuantity} in stock`}
                </span>
              </div>
              <div className="product-detail-actions">
                <button
                  onClick={handleAddToCart}
                  disabled={added || product.inventoryQuantity <= 0}
                  className={`add-to-cart-btn ${added ? 'added' : ''}`}
                >
                  {added ? 'Added to Cart!' : product.inventoryQuantity <= 0 ? 'Out of Stock' : 'Add to Cart'}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </main>
    </div>
  );
}

export default ProductDetailPage;
